import { FC, useState, useMemo, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { Checkbox } from "./ui/checkbox"
import { Badge } from "./ui/badge"
import { ScrollArea } from "./ui/scroll-area"
import { LoadingSpinner } from "./ui/loading-spinner"
import { useGetPlatformDevices } from "@/hooks/api/use-get-platform-devices"
import useAssignDevicesToCustomer from "@/hooks/api/use-assign-devices-to-customer"
import type { PlatformMeterInfoDTO } from "@/gen/api"
import { AssignDevice, AssignDevicePlatformEnum } from "@/gen/api"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

const getDeviceKey = (device: { platformId?: string; platform?: string }) => {
  return `${device.platformId || "unknown"}-${device.platform || "unknown"}`
}
type AssignDeviceDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  councilId: string
  customerId: number
  assignedDevices?: Array<{
    platformId: string
    platform: string
  }>
}

const AssignDeviceDialog: FC<AssignDeviceDialogProps> = ({
  open,
  onOpenChange,
  councilId,
  customerId,
  assignedDevices = [],
}) => {
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedDevices, setSelectedDevices] = useState<PlatformMeterInfoDTO[]>([])
  const queryClient = useQueryClient()

  // Debounce search to avoid API calls on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const sortParams = useMemo(() => {
    return undefined
  }, [])

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetPlatformDevices(
    councilId,
    debouncedSearch,
    sortParams,
  )

  const assignDevicesToCustomer = useAssignDevicesToCustomer({
    onSuccess: () => {
      toast.success("Devices assigned successfully")
      // Invalidate the device list query to trigger a refresh
      queryClient.invalidateQueries({
        queryKey: ["devices", councilId],
      })
      onOpenChange(false)
      setSelectedDevices([])
    },
    onError: (error) => {
      toast.error(`Failed to assign devices: ${error.message}`)
    },
  })

  const allDevices = useMemo(() => {
    return data?.pages.flatMap((page) => page.devices) ?? []
  }, [data])

  const filteredDevices = useMemo(() => {
    return allDevices.filter((device) => {
      const matchesSearch =
        searchTerm === "" ||
        device.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.platformId?.toLowerCase().includes(searchTerm.toLowerCase())

      return matchesSearch
    })
  }, [allDevices, searchTerm])

  const isDeviceSelected = (device: PlatformMeterInfoDTO) => {
    return selectedDevices.some((selected) => getDeviceKey(selected) === getDeviceKey(device))
  }

  const isDeviceAlreadyAssigned = (device: PlatformMeterInfoDTO) => {
    return assignedDevices.some((assigned) => {
      if (assigned.platformId && device.platformId) {
        return assigned.platformId === device.platformId
      }
      if (assigned.platformId && device.name && assigned.platformId === device.name) {
        return true
      }
      if (device.platformId && device.platformId.includes(assigned.platformId)) {
        return true
      }
      return false
    })
  }

  const handleDeviceToggle = (device: PlatformMeterInfoDTO, checked: boolean) => {
    if (checked) {
      setSelectedDevices((prev) => [...prev, device])
    } else {
      setSelectedDevices((prev) => prev.filter((d) => getDeviceKey(d) !== getDeviceKey(device)))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedDevices((prev) => {
        const existingKeys = new Set(prev.map((d) => getDeviceKey(d)))
        const newDevices = filteredDevices.filter(
          (device) => !existingKeys.has(getDeviceKey(device)) && !isDeviceAlreadyAssigned(device),
        )
        return [...prev, ...newDevices]
      })
    } else {
      setSelectedDevices((prev) =>
        prev.filter(
          (device) => !filteredDevices.some((fd) => getDeviceKey(fd) === getDeviceKey(device)),
        ),
      )
    }
  }

  const handleAssign = () => {
    const devices: AssignDevice[] = selectedDevices.map((device) => ({
      platformId: device.platformId || "",
      platform: device.platform as AssignDevicePlatformEnum,
    }))

    assignDevicesToCustomer.mutate({
      councilId,
      assignMultipleDevicesRequest: {
        customerId,
        devices,
      },
    })
  }

  const handleCancel = () => {
    onOpenChange(false)
    setSelectedDevices([])
  }

  const selectedDevicesList = selectedDevices

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col p-6">
        <DialogHeader>
          <DialogTitle>Assign Devices</DialogTitle>
          <DialogDescription>Select devices to assign to this customer</DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-hidden">
          {/* Search */}
          <div>
            <Label htmlFor="search">Search by Name or Platform ID</Label>
            <Input
              id="search"
              placeholder="Enter device name or platform ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Device List */}
          <div className="flex-1 overflow-hidden">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-x-2">
                <Checkbox
                  id="select-all"
                  checked={
                    filteredDevices.length > 0 &&
                    filteredDevices.every((device) => isDeviceSelected(device))
                  }
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all">Select All</Label>
              </div>
              <Badge variant="secondary">{selectedDevices.length} selected</Badge>
            </div>

            <ScrollArea className="h-64 border rounded-md p-3">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : filteredDevices.length === 0 ? (
                <div className="text-center text-muted-foreground py-8">No devices found</div>
              ) : (
                <div className="space-y-3">
                  {filteredDevices.map((device) => {
                    const deviceKey = getDeviceKey(device)
                    const isAssigned = isDeviceAlreadyAssigned(device)
                    const isSelected = isDeviceSelected(device)

                    return (
                      <div
                        key={deviceKey}
                        className={`flex items-center space-x-3 p-3 border rounded-lg ${
                          isAssigned
                            ? "bg-muted/30 opacity-60 cursor-not-allowed"
                            : "hover:bg-muted/50"
                        }`}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={isAssigned}
                          onCheckedChange={(checked) =>
                            !isAssigned && handleDeviceToggle(device, checked as boolean)
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`font-medium truncate ${
                                isAssigned ? "text-muted-foreground" : ""
                              }`}
                            >
                              {device.name || "Unnamed Device"}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {device.platform || "Unknown Platform"}
                            </Badge>
                            {isAssigned && (
                              <Badge variant="secondary" className="text-xs">
                                Already Assigned
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Platform ID: {device.platformId || "N/A"}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </ScrollArea>

            {hasNextPage && (
              <div className="text-center mt-3">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? <LoadingSpinner className="mr-2 size-4" /> : null}
                  Load More
                </Button>
              </div>
            )}
          </div>

          {/* Selected Devices List */}
          {selectedDevicesList.length > 0 && (
            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Selected Devices:</Label>
              <ScrollArea className="h-24 mt-2">
                <div className="space-y-1">
                  {selectedDevicesList.map((device) => {
                    const deviceKey = getDeviceKey(device)
                    return (
                      <div
                        key={deviceKey}
                        className="flex items-center justify-between p-2 bg-muted/30 rounded"
                      >
                        <span className="text-sm truncate flex-1">
                          {device.name || device.platformId}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeviceToggle(device, false)}
                          className="size-6 p-0"
                        >
                          ×
                        </Button>
                      </div>
                    )
                  })}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <Badge variant="outline">
              {selectedDevices.length} device
              {selectedDevices.length !== 1 ? "s" : ""} selected
            </Badge>
            <div className="space-x-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                disabled={selectedDevices.length === 0 || assignDevicesToCustomer.isPending}
              >
                {assignDevicesToCustomer.isPending ? (
                  <LoadingSpinner className="mr-2 size-4" />
                ) : null}
                Assign {selectedDevices.length > 0 && `(${selectedDevices.length})`}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default AssignDeviceDialog
